export default {
  data: [
    {
      id: 'accounts',
      title: 'Accounts',
      description: 'Set up your account, manage details, and configure your business information.',
      graphic: require('@/assets/img/documentation/cards/accounts.png'),
      sections: [
        {
          id: 0,
          title: 'Account Setup',
          questions: [
            {
              id: 0,
              title: 'How do I set up my basic account information?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Setting up your account is the first step to getting started with GrapePerks. This article will guide you through where to find your account settings and what information is required.</p>',
              textAfterImage: '<p class="mb-4">To manage your account details, click on your profile icon in the top-right corner of the navigation bar and select "Account" from the dropdown menu. This will take you to the <a href="/settings/account-details" class="router-link-active">Account details page</a>.</p><p class="mb-4">On this page, you can update your personal and company information. The "Personal information" section allows you to change your name and manage notification preferences. The "Company information" section is where you can update your business details, such as your business name, phone number, and address. Be sure to click "Update" to save any changes you make.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How do I update my business profile?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Keeping your business profile up-to-date is important for customer communications and ensuring your campaigns are delivered correctly.</p>',
              textAfterImage: '<p class="mb-4">To update your details, navigate to the <a href="/settings/account-details" class="router-link-active">Account details page</a> by clicking on your profile icon in the top-right corner and selecting "Account" from the dropdown menu.</p><p class="mb-4">In the "Company information" section, you can edit your business email, phone number, business name, ABN, website, and address. Once you have made your changes, click the "Update" button to save them.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
            {
              id: 2,
              title: 'What information is required to complete my account setup?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">To ensure your account is fully functional and compliant, certain information is required. This article outlines the mandatory fields for your account.</p>',
              textAfterImage: '<p class="mb-4">You can find all the required fields on the <a href="/settings/account-details" class="router-link-active">Account details page</a>. Fields marked with an asterisk (*) are mandatory. This includes your first and last name, business email, phone number, business name, and address. Providing this information is necessary for compliance and to ensure your SMS campaigns are sent correctly.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
          ],
        },
        {
          id: 1,
          title: 'Account Management',
          questions: [
            {
              id: 0,
              title: 'How do I change my password?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">For security, it is a good practice to update your password regularly. This article explains how to change your password.</p>',
              textAfterImage: '<p class="mb-4">To change your password, click on your profile icon in the top-right corner and select "Account" from the dropdown menu. From there, navigate to the "Security" tab where you will find the option to change your password.</p><p class="mb-4">You will need to enter your current password and then your new password twice to confirm. We recommend using a strong, unique password to protect your account.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How do I update my contact information?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Your contact information is used for important account-related notifications. This article shows you how to keep it up-to-date.</p>',
              textAfterImage: '<p class="mb-4">To update it, go to the <a href="/settings/account-details" class="router-link-active">Account details page</a> by clicking your profile icon and selecting "Account". Your contact details, including your email and phone number, are located in the "Personal information" and "Company information" sections. Make any necessary changes and click "Update" to save them.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
            {
              id: 2,
              title: 'Can I have multiple users on my account?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">This article explains the current user account structure and our plans for multi-user support in the future.</p>',
              textAfterImage: '<p class="mb-4">Currently, GrapePerks supports one user login per company account. However, we have designed the account structure to accommodate multiple users in a future update.</p><p class="mb-4">You will notice that "Personal information" and "Company information" are managed in separate sections on the <a href="/settings/account-details" class="router-link-active">Account details page</a>. Your personal information is tied to your individual login, while the company information represents the business profile your customers interact with. This structure will allow us to introduce multi-user functionality in the future, where several team members can have their own logins to manage a single company account.</p><p class="mb-4">Stay tuned for updates on this feature!</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 0,
                },
              ],
            },
          ],
        },
        {
          id: 2,
          title: 'Privacy and Security',
          questions: [
            {
              id: 0,
              title: 'How is my account data protected?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">We take the security of your data very seriously and use industry-standard practices to protect it.</p>',
              textAfterImage: '<p class="mb-4">All your account information and customer data is protected using industry-standard encryption. We regularly update our security measures to ensure your data is safe. For more details, please see our Privacy Policy.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'What security measures are in place?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">We provide several security features that you can control to keep your account secure.</p>',
              textAfterImage: '<p class="mb-4">You can change your password at any time and enable two-factor authentication for an extra layer of security. We also have access controls for team members to ensure everyone only has access to what they need.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 2,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
            {
              id: 2,
              title: 'How can I enable two-factor authentication?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Two-factor authentication (2FA) adds an important extra layer of security to your account. This is how to set it up.</p>',
              textAfterImage: '<p class="mb-4">To enable it, navigate to the "Security" tab in your "Account" settings. Once enabled, you will need to provide a code from your authentication app or SMS to log in. This helps to protect your account even if your password is compromised.</p>',
              relatedQuestions: [
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 1,
                  questionId: 0,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'audience-segments',
      title: 'Audience segments',
      description: 'Import contacts and manage your audiences.',
      graphic: require('@/assets/img/documentation/cards/audience-segments.png'),
      sections: [
        {
          id: 0,
          title: 'Managing your audience',
          questions: [
            {
              id: 0,
              title: 'How to import contacts',
              // img: require('@/assets/img/documentation/audience-segments/import-contacts.jpeg'),
              textBeforeImage: '<p class="mb-4">Importing your contacts is the first step to building your audience. Go to <a href="/contacts">Contact list</a> and click <strong>Import</strong> to start.</p>',
              textAfterImage: '<p class="mb-4"><strong>1. Upload your file:</strong> Drop a CSV or click browse. A quick preview shows how your data (cells) and attributes (columns) are organised. Click <em>Confirm your file</em>.</p>' +
                '<p class="mb-4"><strong>2. Mapping data:</strong> Choose a default country code for phone numbers that do not include one. Map each column to the correct contact attribute — for example, map your email column to <em>Email</em>. Click <em>Confirm mapping</em>.</p>' +
                '<p class="mb-4"><strong>3. Finalise your import:</strong> Optionally add default attributes to all imported records (for example, <em>Tags</em>). Choose whether the contacts are subscribed to marketing and confirm that you meet the compliance conditions below. Click <em>Confirm your import</em>.</p>' +
                '<ul class="mb-4"><li>Contacts explicitly gave permission to receive SMS within the last two years.</li><li>Contacts were not sourced from a third party.</li><li>Contacts were not purchased or rented.</li><li>You agree to comply with applicable laws in Australia, the EU and the USA, and with our terms and policies.</li></ul>' +
                '<p class="mb-4"><strong>4. Import report:</strong> Wait for the import to complete. You will receive a confirmation notification and a report. Contacts can be skipped if they are duplicates or have invalid formats.</p>',
              relatedQuestions: [
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How to create audience segments',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Audience segmentation allows you to group contacts to send targeted and relevant messages. Here is how to create a segment.</p>',
              textAfterImage: '<p class="mb-4">To create an audience segment, go to the "Audience" section in the main menu and select "Segments". Click on "Create segment" to get started. You can then define rules to filter your contacts based on their properties and activities, allowing you to create targeted groups for your campaigns.</p>',
              relatedQuestions: [
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 2,
              title: 'How to manage custom fields',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Custom fields allow you to store extra information about your contacts, which is useful for personalization. This is how to manage them.</p>',
              textAfterImage: '<p class="mb-4">To manage them, go to the "Audience" section in the main menu and select "Custom Fields". Here, you can create new fields to capture data like customer preferences or loyalty status, which can then be used for creating detailed segments and personalizing your campaigns.</p>',
              relatedQuestions: [
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'audience-segments',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 2,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'sms-campaigns',
      title: 'SMS campaigns',
      description: 'Learn how to deliver and schedule SMS campaigns to increase your sales.',
      graphic: require('@/assets/img/documentation/cards/sms-campaigns.png'),
      sections: [
        {
          id: 0,
          title: 'Creating and managing SMS campaigns',
          questions: [
            {
              id: 0,
              title: 'How to create a new SMS campaign',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">This article will guide you through the process of creating and sending a new SMS campaign.</p>',
              textAfterImage: '<p class="mb-4">To create a new SMS campaign, go to the "Campaigns" section in the main menu. From here you can manage your campaigns. Click on "Create campaign" to start the setup process, where you can choose your audience, write your message, and schedule it to be sent.</p>',
              relatedQuestions: [
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How to track campaign performance',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Tracking the performance of your SMS campaigns is crucial to understanding what works. This is how to view your campaign analytics.</p>',
              textAfterImage: '<p class="mb-4">You can track the performance of your SMS campaigns in the "Campaigns" section. Click on any of your sent campaigns to view detailed analytics, including delivery rates, click-through rates, and conversions. This data helps you understand how your audience is engaging with your messages.</p>',
              relatedQuestions: [
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'reviews',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 2,
              title: 'How to use SMS templates',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">SMS templates can save you time and ensure your messaging is consistent. This article explains how to use them.</p>',
              textAfterImage: '<p class="mb-4">SMS templates help you save time by reusing messages. You can manage your templates in the "Campaigns" section under "SMS Templates". Here, you can create new templates and edit existing ones. When creating a new campaign, you can then select a saved template to quickly populate your message.</p>',
              relatedQuestions: [
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'reviews',
      title: 'Reviews',
      description: 'Learn how to collect and manage customer reviews to build social proof and improve your services.',
      graphic: require('@/assets/img/documentation/cards/reviews.png'),
      sections: [
        {
          id: 0,
          title: 'Collecting and managing reviews',
          questions: [
            {
              id: 0,
              title: 'How to request reviews from customers',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Customer reviews are a powerful way to build trust and credibility. This is how to collect reviews from your customers.</p>',
              textAfterImage: '<p class="mb-4">You can automate review requests by navigating to the "Reviews" section in the main menu and going to "Settings". Here, you can set up campaigns to automatically ask for reviews after a customer interacts with your business, helping you collect valuable feedback.</p>',
              relatedQuestions: [
                {
                  categoryId: 'reviews',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How to manage and respond to reviews',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Managing and responding to reviews is just as important as collecting them. This is how to manage your online reputation.</p>',
              textAfterImage: '<p class="mb-4">To manage your reviews, go to the "Reviews" section in the main menu. Here, you can see all the feedback you have received. You can approve, reject, and respond to reviews, allowing you to engage with your customers and manage your online reputation.</p>',
              relatedQuestions: [
                {
                  categoryId: 'reviews',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'waitlist',
      title: 'Waitlist',
      description: 'Manage your waitlist and notify customers when you have availability.',
      graphic: require('@/assets/img/documentation/cards/waiting-list.png'),
      sections: [
        {
          id: 0,
          title: 'Managing your waitlist',
          questions: [
            {
              id: 0,
              title: 'How to complete the waitlist configuration',
              textBeforeImage: '<p class="mb-4">Before you can start using the waitlist feature, you need to complete a 5-step configuration wizard. This ensures your queue is properly set up with all necessary details, messaging templates, and branding.</p><h5 class="mt-6 mb-3">Step 1: Account details</h5><p class="mb-4">Enter your business name, phone number, and address. This information appears on your public waitlist form and in customer notifications. Make sure these details are accurate, as customers will use them to identify and contact your business.</p><h5 class="mt-6 mb-3">Step 2: Message templates</h5><p class="mb-4">Configure the 5 automated SMS messages that are sent to customers at different stages:</p><ul><li><strong>Queue confirmation:</strong> Sent immediately when a customer joins the queue</li><li><strong>Almost ready:</strong> Sent when you mark them as "Ready next"</li><li><strong>It\'s your turn:</strong> Sent when you mark them as "Serving"</li><li><strong>Thanks and review:</strong> Sent when you mark them as "Done" with review request option</li><li><strong>Cancellation turn:</strong> Sent when their turn is cancelled</li></ul><p class="mb-4">Each template can include placeholders like customer name, turn number, and business details. These messages are automatically sent when you change a customer\'s status.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">Step 3: Branding settings</h5><p class="mb-4">Upload your business logo and choose your brand colours. These settings customise the appearance of your public waitlist form, making it consistent with your business identity.</p><h5 class="mt-6 mb-3">Step 4: Company policies</h5><p class="mb-4">Add links to your privacy policy and terms of service. These are required for legal compliance and will be displayed on your public waitlist form. If you haven\'t created these yet, you can do so in your <a href="/account/policies" class="router-link-active">account settings</a>.</p><h5 class="mt-6 mb-3">Step 5: Opening hours</h5><p class="mb-4">Set your business operating hours for each day of the week. This helps customers know when your queue is typically open. You can always manually open or close your queue regardless of these hours.</p><p class="mt-6 mb-4">Once you complete all 5 steps, your waitlist will be ready to use. You can access the configuration settings at any time by clicking the settings icon on the <a href="/waiting-list" class="router-link-active">waitlist page</a>.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 5,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 4,
                },
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
            {
              id: 1,
              title: 'Understanding queue statuses',
              textBeforeImage: '<p class="mb-4">The queue status pill at the top of your waiting list page shows the current state of your queue. This status automatically updates based on whether your workday is open and if you have customers waiting.</p><h5 class="mt-6 mb-3">Queue active (green dot)</h5><p class="mb-4">Your queue is open and accepting new customers. Customers can join through your public form, and they\'ll receive an automatic confirmation message. This status appears when you\'ve started your workday.</p><h5 class="mt-6 mb-3">Queue closed (red dot)</h5><p class="mb-4">Your queue is closed and not accepting new customers. There are no customers waiting to be served. This status appears when you\'ve ended your workday and all customers have been served or cancelled.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">Queue closed with pending (orange dot)</h5><p class="mb-4">Your queue is closed to new customers, but you still have customers waiting from earlier. This status appears when you\'ve ended your workday but haven\'t finished serving all customers. You can still serve these pending customers even though the queue is closed to new entries.</p><h5 class="mt-6 mb-3">Changing queue status</h5><p class="mb-4">Click the queue status pill to see available actions:</p><ul><li><strong>Begin workday:</strong> Opens your queue to start accepting customers</li><li><strong>End workday:</strong> Closes your queue to new customers (existing customers remain in the queue)</li><li><strong>Start fresh:</strong> Opens a new workday and clears any pending customers from yesterday</li></ul><p class="mb-4">The status automatically reflects whether you have pending customers, so you always know the true state of your queue at a glance.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 4,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
              ],
            },
            {
              id: 2,
              title: 'Understanding customer statuses',
              textBeforeImage: '<p class="mb-4">Each customer in your queue has a status that shows where they are in the service process. You can change these statuses as you serve customers, and the system will automatically send the appropriate notification message.</p><h5 class="mt-6 mb-3">Waiting (orange badge)</h5><p class="mb-4">The customer has just joined the queue and is waiting for their turn. This is the initial status when someone fills out your public form. They\'ve already received a confirmation message with their queue number.</p><h5 class="mt-6 mb-3">Ready next (grey badge with bell icon)</h5><p class="mb-4">The customer is next in line or will be served soon. Use this status to give customers a heads-up that their turn is coming. They\'ll automatically receive an "Almost ready" notification when you set this status.</p><h5 class="mt-6 mb-3">Serving (purple badge)</h5><p class="mb-4">The customer is currently being served. Change to this status when you\'re actively helping them. They\'ll receive an "It\'s your turn" notification to let them know you\'re ready for them.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">Done (green badge with check)</h5><p class="mb-4">The customer has been served and their turn is complete. When you mark someone as done, you\'ll have the option to send a review request message or just mark them as complete without a message. Customers marked as done appear in the "Served" tab.</p><h5 class="mt-6 mb-3">Cancelled (red badge with X)</h5><p class="mb-4">The customer\'s turn has been cancelled. This could be because they didn\'t show up, requested to be removed, or you needed to cancel for another reason. When you individually change a customer\'s status to "Cancelled", they\'ll receive a cancellation notification automatically. Cancelled customers appear in the "Cancelled" tab.</p><h5 class="mt-6 mb-3">Changing customer status</h5><p class="mb-4">To change a customer\'s status, click the status badge in their row. You\'ll see a dropdown with all available statuses. Select the new status and confirm your choice. The system will handle the rest, including sending the appropriate notification message.</p><p class="mb-4">The typical flow is: Waiting → Ready next → Serving → Done. However, you can move customers to any status at any time, and you can cancel them from any stage.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'reviews',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
            {
              id: 3,
              title: 'How to manage customers on the waitlist',
              textBeforeImage: '<p class="mb-4">The waitlist page provides a complete view of all your customers organised by their current status. You can change statuses, send messages, and track who\'s been served.</p><h5 class="mt-6 mb-3">Three tabs for organisation</h5><p class="mb-4">Your customers are automatically organised into three tabs:</p><ul><li><strong>Active:</strong> All customers currently in the queue (includes "Waiting", "Ready next", and "Serving" statuses)</li><li><strong>Served:</strong> Customers who have been marked as "Done"</li><li><strong>Cancelled:</strong> Customers whose turns were cancelled</li></ul><p class="mb-4">Each tab shows a count so you can quickly see how many customers are in each state.</p><h5 class="mt-6 mb-3">Changing customer status</h5><p class="mb-4">Click on any customer\'s status badge to see available status options. Select a new status and confirm your choice. The customer will automatically receive the appropriate notification message based on their new status.</p>',
              textAfterImage: '<p class="mb-4">When marking a customer as "Done", you\'ll see a special prompt asking if you want to send a review request. Choose "Send review request" to include your review link in the completion message, or "Mark as done" to complete without requesting a review.</p><h5 class="mt-6 mb-3">Sending manual messages</h5><p class="mb-4">While status changes trigger automatic messages, you can also send custom messages to any customer. Click the message icon in their row to compose and send a personalised SMS. This is useful for special circumstances or additional communication.</p><h5 class="mt-6 mb-3">Viewing customer details</h5><p class="mb-4">Click on any customer row to view their full details, including their name, phone number, when they arrived, and how long they\'ve been waiting. On mobile devices, tapping a customer opens a detailed modal with all information and action buttons.</p><h5 class="mt-6 mb-3">Refresh and real-time updates</h5><p class="mb-4">Click the refresh icon at any time to get the latest data. The page also receives real-time updates when customers join the queue or when changes are made from other devices, so you\'re always seeing current information.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'reviews',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
            {
              id: 4,
              title: 'How to open and close your workday',
              textBeforeImage: '<p class="mb-4">Opening and closing your workday controls whether your queue accepts new customers. This gives you complete control over when people can join your waitlist.</p><h5 class="mt-6 mb-3">Beginning your workday</h5><p class="mb-4">To start accepting customers, click the queue status pill at the top of the page and select "Begin workday". Your queue status will change to "Queue active" (green dot), and customers can now join through your public form. They\'ll receive automatic confirmation messages when they join.</p><p class="mb-4">If you have pending customers from yesterday and want to start fresh, select "Queue will open fresh and clear pending customers" instead. This opens your queue and clears any customers left over from the previous day. Note: Customers cleared this way will not receive notifications.</p><h5 class="mt-6 mb-3">Ending your workday</h5><p class="mb-4">When you\'re done for the day, click the queue status pill and select "Queue closed". A confirmation dialog will appear explaining what happens next.</p>',
              textAfterImage: '<p class="mb-4">When you end your workday:</p><ul><li>New customers can no longer join the queue</li><li>Existing customers remain in the queue</li><li>You can still serve and manage customers who are waiting</li><li>Your queue status shows "Queue closed" (red) or "Queue closed with pending" (orange) depending on whether customers are still waiting</li></ul><h5 class="mt-6 mb-3">Managing pending customers</h5><p class="mb-4">If you end your workday with customers still waiting, they remain in the queue. You can:</p><ul><li>Continue serving them even though the queue is closed to new entries</li><li>Cancel them individually by changing their status to "Cancelled" (they will receive a cancellation notification)</li><li>Start fresh the next day, which clears all pending customers (they will not receive notifications)</li></ul><p class="mb-4">When you cancel customers individually by changing their status to "Cancelled", they automatically receive a notification message letting them know their turn has been cancelled. However, customers who are cleared when starting fresh do not receive notifications.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 5,
                },
              ],
            },
            {
              id: 5,
              title: 'Understanding fair queue rejoining',
              textBeforeImage: '<p class="mb-4">When a customer who was previously served or cancelled decides to rejoin your queue, GrapePerks uses a fair queueing system to ensure no one feels like they\'re cutting in line.</p><h5 class="mt-6 mb-3">Why fair queueing matters</h5><p class="mb-4">Imagine you have three customers in your queue: Sarah at position 1, John at position 2, and Emma at position 3. If you cancel John and then immediately change his status back to "Waiting", it would be unfair for John to return to position 2 ahead of Emma who has been patiently waiting.</p><h5 class="mt-6 mb-3">How the system works</h5><p class="mb-4">When you change a customer\'s status from "Served" or "Cancelled" to any active queue status ("Waiting", "Ready next", or "Serving"), the system automatically:</p><ul><li>Assigns them a new position at the end of the queue</li><li>Sends them an automatic notification with their new queue number</li><li>Shows you a confirmation dialog explaining the placement</li></ul><p class="mb-4">The customer receives the same "Queue confirmation" SMS they would have received if they had joined fresh, including their new turn number and estimated wait time.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">Rejoin scenarios</h5><p class="mb-4">This fair queueing applies whenever you move someone from a completed state back to an active state:</p><ul><li><strong>Cancelled → Waiting:</strong> Customer changed their mind and wants to wait again</li><li><strong>Served → Waiting:</strong> Customer needs an additional service</li><li><strong>Cancelled → Ready next:</strong> Customer returned just in time to be served next</li><li><strong>Served → Serving:</strong> Customer needs immediate follow-up service</li></ul><h5 class="mt-6 mb-3">What the customer sees</h5><p class="mb-4">When you rejoin a customer:</p><ul><li>They receive an SMS: "You\'re back in the queue! Your new position is #X with Y people ahead of you."</li><li>If they\'re viewing their confirmation page, it updates within 15 seconds to show their new active status</li><li>They can see their updated queue position and estimated wait time</li></ul><h5 class="mt-6 mb-3">Multiple rejoins</h5><p class="mb-4">If the same customer rejoins multiple times, they will always be placed at the end of the queue. This ensures fairness for everyone who has been waiting continuously.</p><h5 class="mt-6 mb-3">Real-time notifications</h5><p class="mb-4">The automatic notification system ensures customers are always informed when their status changes, whether they\'re being served, cancelled, or rejoining the queue.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 4,
                },
              ],
            },
            {
              id: 6,
              title: 'Understanding the Active tab and customer states',
              textBeforeImage: '<p class="mb-4">The "Active" tab in your waitlist shows all customers who are currently in your queue, regardless of their specific status. This tab was designed to give you a complete view of everyone who needs to be served.</p><h5 class="mt-6 mb-3">Why "Active" instead of "Waiting"?</h5><p class="mb-4">The tab was renamed from "Waiting" to "Active" for clarity. It contains three different customer statuses, and calling it "Waiting" was confusing when customers marked as "Serving" were also visible there.</p><h5 class="mt-6 mb-3">Three statuses in the Active tab</h5><p class="mb-4">The Active tab shows customers with these statuses:</p><ul><li><strong>Waiting (orange):</strong> Customer has joined and is waiting their turn</li><li><strong>Ready next (grey with bell):</strong> Customer is next or close to being served</li><li><strong>Serving (purple):</strong> Customer is currently being attended to</li></ul><p class="mb-4">All three of these statuses represent customers who are actively in your queue and occupy a position that other customers see as "spots ahead".</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">How customers move through states</h5><p class="mb-4">The typical customer journey is:</p><ol><li>Joins queue → <strong>Waiting</strong></li><li>Nearly their turn → <strong>Ready next</strong> (optional)</li><li>Being served → <strong>Serving</strong></li><li>Service complete → <strong>Done</strong> (moves to Served tab)</li></ol><p class="mb-4">However, you can move customers to any status at any time based on your workflow. Some businesses skip "Ready next" and go straight from "Waiting" to "Serving".</p><h5 class="mt-6 mb-3">Active vs Completed</h5><p class="mb-4">The key difference is that Active customers are still in the queue and occupying a position, while completed customers (Served or Cancelled) have been removed from the queue and no longer count toward other customers\' wait times.</p><h5 class="mt-6 mb-3">Tab counts</h5><p class="mb-4">The number next to the Active tab shows the total count of all three statuses combined. This gives you a quick view of how many customers still need attention.</p><h5 class="mt-6 mb-3">Queue management tip</h5><p class="mb-4">Keep the Active tab clear by marking customers as "Done" once they\'ve been served. This helps you focus on who still needs attention and gives waiting customers accurate wait time estimates.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 2,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 1,
                },
              ],
            },
            {
              id: 7,
              title: 'Real-time updates and customer notifications',
              textBeforeImage: '<p class="mb-4">Your waitlist uses real-time technology to keep both you and your customers updated instantly when anything changes in the queue.</p><h5 class="mt-6 mb-3">Automatic customer notifications</h5><p class="mb-4">Customers receive automatic SMS notifications when:</p><ul><li><strong>They join the queue:</strong> Confirmation with their queue number and position</li><li><strong>Status changes to "Ready next":</strong> "Almost ready" notification</li><li><strong>Status changes to "Serving":</strong> "It\'s your turn" notification</li><li><strong>Status changes to "Done":</strong> Thank you message (optionally with review request)</li><li><strong>Status changes to "Cancelled":</strong> Cancellation notification</li><li><strong>They rejoin the queue:</strong> New queue confirmation with updated position</li></ul><p class="mb-4">These notifications are sent automatically based on the message templates you configured during setup. You can customize these templates at any time from the configuration page.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">Dashboard real-time updates</h5><p class="mb-4">Your dashboard receives instant updates when:</p><ul><li>A new customer joins through your public form</li><li>Another staff member changes a customer\'s status</li><li>A customer\'s waiting time increases</li></ul><p class="mb-4">If you have your dashboard open on multiple devices or multiple staff members are managing the queue, everyone sees the same up-to-date information instantly.</p><h5 class="mt-6 mb-3">Customer confirmation page updates</h5><p class="mb-4">When customers access their confirmation page (via the link in their SMS), they see:</p><ul><li>Their current queue position</li><li>Number of people ahead of them</li><li>Their estimated wait time</li><li>Real-time status updates</li></ul><p class="mb-4">The page polls for updates every 15 seconds, so customers see changes within moments of you making them.</p><h5 class="mt-6 mb-3">Manual refresh</h5><p class="mb-4">While updates happen automatically, you can also manually refresh your dashboard at any time by clicking the refresh icon. This is useful if you want to ensure you\'re seeing the absolute latest data.</p><h5 class="mt-6 mb-3">Notification customization</h5><p class="mb-4">You can customize all notification messages from your waitlist configuration page. Each message template supports placeholders like customer name, queue number, business name, and address to make messages personal and informative.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 3,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 2,
                },
              ],
            },
            {
              id: 8,
              title: 'How to use the public form and QR code',
              textBeforeImage: '<p class="mb-4">Your public waitlist form is how customers join your queue. You can share it through various channels and even display a QR code for easy in-person access.</p><h5 class="mt-6 mb-3">Accessing your public form</h5><p class="mb-4">Click the "Public form" button (document icon) at the top of your <a href="/waitlist" class="router-link-active">waitlist page</a>. This opens a view showing your unique QR code and public form link. Your form is hosted on a unique URL that you can share with customers.</p><h5 class="mt-6 mb-3">Downloading the QR code</h5><p class="mb-4">The QR code can be downloaded and printed for display in your business location. When customers scan it with their phone camera, they\'re taken directly to your waitlist form. This is perfect for walk-in customers who want to join the queue without needing to speak to staff.</p>',
              textAfterImage: '<h5 class="mt-6 mb-3">How customers join the queue</h5><p class="mb-4">When customers access your public form (via QR code or direct link), they\'ll see a form branded with your logo and colours. They need to provide:</p><ul><li>Their full name</li><li>Their phone number for notifications</li><li>Acceptance of your privacy policy and terms of service</li></ul><p class="mb-4">After submitting the form, they immediately receive an SMS confirmation with their queue number and position. They\'ll continue to receive automatic updates as their turn approaches.</p><h5 class="mt-6 mb-3">Sharing your form</h5><p class="mb-4">You can share your waitlist form in multiple ways:</p><ul><li><strong>QR code:</strong> Print and display in your location</li><li><strong>Direct link:</strong> Share via email, social media, or your website</li><li><strong>SMS campaign:</strong> Send the link to your contact list to let them know you\'re accepting queue entries</li></ul><p class="mb-4">Your form is always available, but customers can only join when your queue status is "Queue active". When your queue is closed, the form displays a message letting them know you\'re not currently accepting entries.</p>',
              relatedQuestions: [
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 4,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'compliance',
      title: 'Compliance',
      description: 'Understand the legal requirements for sending SMS messages and managing customer data.',
      graphic: require('@/assets/img/documentation/cards/compliance.png'),
      sections: [
        {
          id: 0,
          title: 'Legal requirements and best practices',
          questions: [
            {
              id: 0,
              title: 'What are the legal requirements for sending SMS messages?',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">Understanding the legal requirements for sending SMS messages is essential for running compliant and effective campaigns.</p>',
              textAfterImage: '<p class="mb-4">Complying with legal requirements for SMS marketing is crucial. Key regulations like the Spam Act (Australia), GDPR (EU), and TCPA (USA) require you to get clear consent from users before sending messages and to provide an easy way to opt out. It is your responsibility to ensure your campaigns are compliant with the laws in your region.</p>',
              relatedQuestions: [
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 1,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 0,
                },
                {
                  categoryId: 'sms-campaigns',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
            {
              id: 1,
              title: 'How to create a privacy policy',
              // img: require('@/assets/img/vuexy/pages/kb-article.jpg'),
              textBeforeImage: '<p class="mb-4">A privacy policy is a legal document that is crucial for complying with data privacy regulations and building trust with your customers.</p>',
              textAfterImage: '<p class="mb-4">A privacy policy is essential for legal compliance and building customer trust. To create yours, go to your "Account" settings, and then to the "Policies" tab. We provide a template to help you get started, but we recommend consulting with a legal professional to ensure it meets the requirements for your region. Your policy will be hosted on a unique URL that you can share with your customers.</p>',
              relatedQuestions: [
                {
                  categoryId: 'compliance',
                  sectionId: 0,
                  questionId: 0,
                },
                {
                  categoryId: 'accounts',
                  sectionId: 2,
                  questionId: 1,
                },
                {
                  categoryId: 'waitlist',
                  sectionId: 0,
                  questionId: 0,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  selectedCategory: null,
  selectedSection: null,
  selectedQuestion: null,
};